import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { MAX_ZNAKOW_OPISU } from "@shared/ocenaTypy.js";
import { useKatalogParametrow } from "../../hooks/useKatalogParametrow.js";
import { useKonto } from "../../hooks/useKonto.js";
import { api, ApiError, type OdpowiedzPrzyjecia, type ProviderLists } from "../../lib/api.js";
import { odczytajCzasNagrania } from "../../lib/czasNagrania.js";
import { formatujRozmiar } from "../../lib/formatNagrania.js";
import {
  bladSerweraNaPole,
  etykietaPrzyciskuOceny,
  etykietaPrzyciskuWysylki,
  KOMUNIKAT_BRAKU_OPISU,
  KOMUNIKAT_BRAKU_OPISU_UZYTKOWNIKA,
  KOMUNIKAT_NAGRANIA_WYLACZONE,
  pierwszeZBledem,
  podgladTekstu,
  potwierdzenieWgrania,
  potwierdzenieWgraniaUzytkownika,
  TRYBY_WEJSCIA,
  TRYBY_WEJSCIA_UZYTKOWNIKA,
  tekstBaneruNazwisk,
  tekstRozpoznaniaDlaUzytkownika,
  tekstWymaganNagrania,
  walidujCzasNagrania,
  walidujFormularz,
  walidujNagranie,
  type BledyFormularza,
  type PoleFormularza,
  type TrybWejscia,
} from "../../lib/formularzWejscia.js";
import { czyDlugieNagranie, tekstOstrzezeniaKosztu, wyborEkspresjiDlaNagrania } from "../../lib/ostrzezenieKosztu.js";
import { domyslnyWybor, uporzadkujWybor } from "../../lib/parametryOceny.js";
import {
  czytajPlikTekstowy,
  POMOC_PLIKU_TEKSTOWEGO,
  walidujPlikTekstowy,
} from "../../lib/plikTekstowy.js";
import {
  AnulowanoWgrywanie,
  pobierzLimity,
  porzucZapamietaneWgrywanie,
  POSTEP_PUSTY,
  wyslijNagranie,
  type FazaWgrywania,
  type LimityWgrywania,
  type PostepWgrywania,
  type WynikLimitow,
} from "../../lib/wgrywanie.js";
import { IconInformacja, IconOstrzezenie, IconPtaszek } from "../icons/index.js";
import { ProviderPicker } from "../transcript/ProviderPicker.js";
import { Badge } from "../ui/Badge.js";
import { Banner } from "../ui/Banner.js";
import { Button } from "../ui/Button.js";
import { Card } from "../ui/Card.js";
import { Checkbox } from "../ui/Choice.js";
import { Dialog } from "../ui/Dialog.js";
import { Field, Input, Textarea } from "../ui/Field.js";
import { type OpcjaSelect, Select } from "../ui/Select.js";
import { SegmentedControl } from "../ui/SegmentedControl.js";
import { Spinner } from "../ui/Spinner.js";
import { StanBledu, StanLadowania, StanPusty } from "../ui/stany.js";
import { useToast } from "../ui/Toast.js";
import { ListaPlikow, UploadZone } from "../ui/UploadZone.js";
import { WyborMowcy } from "../uzytkownik/WyborMowcy.js";
import { PostepWysylania } from "./PostepWysylania.js";
import { WyborParametrow } from "./WyborParametrow.js";

// Limity nagran (rozmiar, godziny, rozmiar porcji) przychodza z GET /api/sessions/meta/limity (lib/wgrywanie.ts),
// a starszy serwer bez tej trasy dostaje tam swoje 200 MB. Klient nie ma wlasnej stalej: gdy limitow nie da sie
// wczytac, podpowiedz jest bez liczby, a rozmiar sprawdza serwer (UP-1: wymagania widoczne od razu, ale nie zmyslone).

const RETENCJE: OpcjaSelect[] = [
  { wartosc: "DELETE_AFTER_ANALYSIS", etykieta: "Skasować od razu po analizie" },
  { wartosc: "KEEP_30D", etykieta: "Zostawić na 30 dni" },
  { wartosc: "KEEP", etykieta: "Zostawić" },
];

function kodBledu(err: unknown): string {
  return err instanceof ApiError ? `${err.status} ${err.code}` : "brak połączenia z serwerem";
}

// Opis niesie tez etap relacji (pierwsza rozmowa czy kolejne spotkanie osob, ktore sie znaja): ocena bierze go pod
// uwage; wie o nim z opisu albo z samej rozmowy, jesli rozmowcy o tym wspominaja, wiec opis jest najpewniejszym
// zrodlem. Dlatego o znajomosc prosi i przyklad w polu (znika po wpisaniu), i podpowiedz pod polem w kazdym wariancie
// (F-1: placeholder nie jest jedynym nosnikiem). Podpowiedz pyta, kto z kim juz sie zna albo rozmawial: przy trzech
// i wiecej osobach znajomosc bywa czesciowa, a "tak" za wszystkich zrobiloby znajomego z osoby, ktora dopiero poznaja.
// Przyklad miesci sie w 4 wierszach pola (rows=4) juz od 320 px szerokosci ekranu (Inter 14 px, zmierzone 02.11.2026):
// placeholder sie nie przewija, wiec dluzszy ucina koncowke na telefonie. Od 03.11.2026 przyklad pokazuje role, nie imie:
// ludzie kopiuja wzor, a imie w opisie to dana osobowa, ktora jedzie do modelu. Jest krotszy, wiec miesci sie tym bardziej.
const OPIS_PLACEHOLDER =
  "Kto rozmawia, w jakich rolach, o czym i czy już się znają, np. handlowiec drugi raz rozmawia z klientem o ofercie";

/** Przyklad w polu tekstu rozmowy: podpisy to role, bez imion i nazwy firmy (z tego samego powodu co opis wyzej). */
const TEKST_PLACEHOLDER = "Handlowiec: Dzień dobry, dzwonię w sprawie oferty.\nKlient: Dzień dobry, słucham.";

/**
 * Karta "Nowa analiza": trzy wejscia (nagranie, plik tekstowy, wklejony tekst), opis rozmowy,
 * wybor parametrow oceny i zgoda rozmowcow. Widok uzytkownika ma dwa wejscia, bez nagrania
 * (TRYBY_WEJSCIA_UZYTKOWNIKA): nagrania kont uzytkownikow wroca z ekranem sprawdzenia zaslepienia.
 *
 * - W trybach tekstowych znikaja wybor modeli transkrypcji i retencja nagrania, a brak modelu
 *   transkrypcji nie blokuje wgrywania tekstu: stan listy modeli dotyczy WYLACZNIE trybu nagrania.
 * - Formularz sam rozpoznaje, czy tekst ma podpisy mowcow (tym samym parserem co serwer) i pokazuje
 *   "Rozpoznano N mowcow, M wypowiedzi". Przy tekscie bez podpisow opis rozmowy jest obowiazkowy.
 * - Bledy pol to komunikaty przy polach (F-12), nie wygaszony przycisk. Kody bledow serwera
 *   wracaja jako zdania przy tym polu, ktorego dotycza.
 */
export function FormularzAnalizy({
  onWgrano,
  wariant = "administrator",
  stronaMaBaner = false,
}: {
  /**
   * Wywolane po przyjeciu analizy przez serwer. Dostaje odpowiedz przyjecia (z id nowej analizy): administrator
   * odswieza liste, uzytkownik przechodzi na ekran wyniku. BEZ argumentu wolane jest wtedy, gdy nagranie dotarlo, ale
   * serwer nie potwierdzil przyjecia (analiza mogla powstac): widok ma tylko odswiezyc to, co pokazuje.
   */
  onWgrano: (odpowiedz?: OdpowiedzPrzyjecia) => void | Promise<void>;
  /**
   * "administrator" (domyslnie): jedna karta "Nowa analiza" z wyborem parametrow oceny i (dla admina) modeli.
   * "uzytkownik": widok konta `user` z makiety "Nowa rozmowa": karty 1. Skad jest rozmowa, 2. Kim jestes, zgoda i
   * przycisk "Oceń rozmowę". Bez wyboru parametrow (ida wszystkie wlaczone z katalogu), bez modeli, kosztow i zargonu.
   */
  wariant?: "administrator" | "uzytkownik";
  /**
   * true: strona pokazuje nad formularzem wlasny baner stanu (lista analiz: "Nie udaje się odświeżyć listy"). BN-3:
   * baner o nazwiskach ustepuje mu wtedy miejsca, tak jak ostrzezeniu o koszcie.
   */
  stronaMaBaner?: boolean;
}): React.JSX.Element {
  const toast = useToast();
  const uzytkownik = wariant === "uzytkownik";
  // Konto `user` nie wybiera modeli i nie widzi kosztow: modele ida z ustawien globalnych, a trasy /api/settings i
  // /api/sessions/meta/providers sa dla niego zamkniete (403), dlatego formularz ich wtedy nie pyta. Widok
  // uzytkownika nie pyta o nie tez wtedy, gdy ktos wyrenderuje go w roli administratora (np. w tescie).
  const { czyAdmin: adminZKonta } = useKonto();
  const czyAdmin = adminZKonta && !uzytkownik;
  const formularzRef = useRef<HTMLFormElement>(null);

  // Rodzaje materialu tego widoku. Lista jest jedynym przelacznikiem: tryb spoza niej nie da sie wybrac ani wyslac, wiec
  // z widoku uzytkownika (bez nagrania) nagranie nie wychodzi zadna droga. Serwer i tak odmawia kontu uzytkownika 403.
  const trybyWidoku = uzytkownik ? TRYBY_WEJSCIA_UZYTKOWNIKA : TRYBY_WEJSCIA;
  const czyTrybWidoku = (t: TrybWejscia): boolean => trybyWidoku.some((w) => w.wartosc === t);

  const [tryb, setTryb] = useState<TrybWejscia>(uzytkownik ? "tekst" : "nagranie");

  // --- modele (tylko nagranie) ---
  const [providers, setProviders] = useState<ProviderLists | null>(null);
  const [bladDostawcow, setBladDostawcow] = useState<string | null>(null);
  const [stt, setStt] = useState("");
  const [expression, setExpression] = useState("");
  const [sttModel, setSttModel] = useState("");
  const [expressionModel, setExpressionModel] = useState("");
  const [retention, setRetention] = useState("DELETE_AFTER_ANALYSIS");
  const [dialogModeli, setDialogModeli] = useState(false);

  // --- wejscie ---
  const [plikAudio, setPlikAudio] = useState<File | null>(null);
  const [plikTekstu, setPlikTekstu] = useState<{ plik: File; tekst: string } | null>(null);
  const [tekst, setTekst] = useState("");
  const [tytul, setTytul] = useState("");
  const [opis, setOpis] = useState("");
  const [zgoda, setZgoda] = useState(false);
  /** Widok uzytkownika: nazwa mowcy z tekstu z podpisami, ktora wybral jako "Ty". null: nie wybrano nikogo. */
  const [ja, setJa] = useState<string | null>(null);
  /** Wybor parametrow zmieniony przez uzytkownika. null: domyslny (wszystkie wlaczone). */
  const [recznyWybor, setRecznyWybor] = useState<string[] | null>(null);

  const [bledy, setBledy] = useState<BledyFormularza>({});
  const [bladOgolny, setBladOgolny] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [fokus, setFokus] = useState<{ pole: PoleFormularza; numer: number } | null>(null);

  // --- duze nagrania: limity serwera, czas nagrania, postep wysylania ---
  /** null: jeszcze nie wczytane albo wczytanie sie nie udalo (wtedy podpowiedz bez liczby). */
  const [limity, setLimity] = useState<WynikLimitow | null>(null);
  const limityRef = useRef<LimityWgrywania | null>(null);
  /** Czas nagrania z metadanych przegladarki, razem z plikiem, ktorego dotyczy (null: nieznany albo odczyt trwa). */
  const [czasPliku, setCzasPliku] = useState<{ plik: File; sek: number | null } | null>(null);
  /** Uzytkownik sam ruszyl wybor oceny brzmienia: wtedy zadna automatyka go nie nadpisuje. */
  const [ekspresjaRecznie, setEkspresjaRecznie] = useState(false);
  const [postep, setPostep] = useState<PostepWgrywania | null>(null);
  const kontrolerRef = useRef<AbortController | null>(null);
  const zamontowanyRef = useRef(true);
  /** Faza biezacego wysylania (null: nic nie leci). Sprzatanie komponentu sprawdza ja, zanim cokolwiek przerwie. */
  const fazaRef = useRef<FazaWgrywania | null>(null);
  const plikAudioRef = useRef<File | null>(null);
  plikAudioRef.current = plikAudio;
  const uzytkownikRef = useRef(uzytkownik);
  uzytkownikRef.current = uzytkownik;
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const { katalog, odswiez: ponowKatalog } = useKatalogParametrow();

  const wybrane = useMemo(() => {
    if (katalog.stan !== "gotowy") return [];
    const lista = katalog.katalog.soczewki;
    return recznyWybor === null ? domyslnyWybor(lista) : uporzadkujWybor(recznyWybor, lista);
  }, [katalog, recznyWybor]);

  /**
   * Wczesniej `catch { setProviders({stt:[],expression:[]}) }` mialo skutek gorszy niz
   * pusty stan: warunek `providers &&` byl prawdziwy, wiec formularz renderowal sie
   * w calosci z pustym wyborem modeli i pozwalal kliknac "Analizuj".
   */
  const pobierzDostawcow = useCallback(async () => {
    setProviders(null);
    setBladDostawcow(null);
    if (!czyAdmin) {
      // Puste pola modeli znacza dla serwera "uzyj ustawien globalnych", a dla usera serwer i tak ignoruje wybor.
      // Lista jest pusta celowo: nic z niej nie pokazujemy (patrz `brakModeli` nizej).
      setProviders({ stt: [], expression: [], ser: [] });
      setEkspresjaRecznie(false);
      return;
    }
    try {
      const [lists, saved] = await Promise.all([api.providers(), api.settings()]);
      setProviders(lists);
      // Formularz startuje od ustawien globalnych, ale wybor tutaj dotyczy tylko tej
      // jednej analizy - inaczej nie dalo by sie porownac modeli bez przestawiania
      // calej aplikacji.
      setStt(saved.settings.sttProvider);
      setExpression(saved.settings.expressionProvider);
      setSttModel(saved.settings.sttModel ?? "");
      setExpressionModel(saved.settings.expressionModel ?? "");
      setRetention(saved.settings.defaultAudioRetention);
      // Wybor wrocil do ustawien, wiec przestaje byc "reczny": dlugie nagranie znowu dostanie domyslne "bez oceny".
      setEkspresjaRecznie(false);
    } catch (err) {
      setBladDostawcow(kodBledu(err));
    }
  }, [czyAdmin]);

  useEffect(() => {
    void pobierzDostawcow();
  }, [pobierzDostawcow]);

  // Limity serwera do podpowiedzi i walidacji na miejscu. Blad (siec, 401) zostawia `null`: podpowiedz bez liczby,
  // a wysylka zapyta o limity jeszcze raz - nic nie jest zgadywane.
  const wczytajLimity = useCallback((): void => {
    pobierzLimity().then(
      (wynik) => {
        if (zamontowanyRef.current) setLimity(wynik);
      },
      () => undefined,
    );
  }, []);

  useEffect(() => {
    wczytajLimity();
  }, [wczytajLimity]);

  useEffect(() => {
    limityRef.current = limity?.limity ?? null;
  }, [limity]);

  // Wyjscie ze strony w trakcie wysylania przerywa je (i porzuca wgrywanie na serwerze): w tle nic nie wisi niewidoczne,
  // a uzytkownik od razu dowiaduje sie, ze nagranie nie dotarlo. Zamkniecie karty chroni ostrzezenie przegladarki nizej.
  // WYJATEK: gdy wszystkie porcje juz dotarly (faza skladania), przerwanie niczego nie cofa - serwer i tak zaklada analize.
  // Wtedy nie przerywamy i nie mowimy o przerwaniu (K-1): toast jest neutralny, a o wyniku powie drugi, po zakonczeniu.
  useEffect(() => {
    zamontowanyRef.current = true;
    return () => {
      zamontowanyRef.current = false;
      const kontroler = kontrolerRef.current;
      if (kontroler && fazaRef.current === "skladanie") {
        toastRef.current.informacja(
          uzytkownikRef.current
            ? "Wysyłanie zakończone, ocena powstaje. Sprawdź listę w zakładce Moje rozmowy."
            : "Wysyłanie zakończone, analiza powstaje. Sprawdź listę analiz.",
        );
      } else if (kontroler) {
        kontrolerRef.current = null;
        kontroler.abort();
        toastRef.current.informacja("Wysyłanie nagrania zostało przerwane, bo opuszczono tę stronę. Wgraj nagranie jeszcze raz.");
      } else if (plikAudioRef.current) {
        // Nic nie leci, ale po bledzie polaczenia mogla zostac na serwerze czesc pliku: plik z pamieci strony znika,
        // wiec wgrywania nikt juz nie wznowi.
        porzucZapamietaneWgrywanie(plikAudioRef.current);
      }
    };
  }, []);

  // Wysylka duzego nagrania trwa minuty, a nawet godziny: przypadkowe zamkniecie karty traci wszystko, wiec pytamy.
  useEffect(() => {
    if (!busy || tryb !== "nagranie") return;
    const zapytaj = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", zapytaj);
    return () => window.removeEventListener("beforeunload", zapytaj);
  }, [busy, tryb]);

  // Czas nagrania z metadanych (ostrzezenie o koszcie, wczesne odrzucenie nagrania dluzszego niz limit). Wynik dotyczy
  // konkretnego pliku, wiec spozniony odczyt poprzedniego pliku jest ignorowany.
  useEffect(() => {
    if (!plikAudio) return;
    let aktualny = true;
    void odczytajCzasNagrania(plikAudio).then((sek) => {
      if (!aktualny) return;
      const blad = walidujCzasNagrania(sek, limityRef.current);
      if (blad) {
        setPlikAudio(null);
        setBledy((b) => ({ ...b, plik: blad }));
        return;
      }
      // Nieznany czas to to samo co brak wpisu (patrz `czasSek` nizej), wiec nie ma po co ruszac stanu.
      if (sek !== null) setCzasPliku({ plik: plikAudio, sek });
    });
    return () => {
      aktualny = false;
    };
  }, [plikAudio]);

  // Po nieudanej walidacji przewijamy do pierwszego pola z bledem i dajemy mu fokus (A-5).
  useEffect(() => {
    if (!fokus) return;
    const sekcja = formularzRef.current?.querySelector<HTMLElement>(`[data-pole="${fokus.pole}"]`);
    if (!sekcja) return;
    if (typeof sekcja.scrollIntoView === "function") sekcja.scrollIntoView({ block: "center" });
    sekcja.querySelector<HTMLElement>("textarea, input, [role='button']")?.focus({ preventScroll: true });
  }, [fokus]);

  // --- podglad tekstu ---
  // Parsowanie 400 tys. znakow na kazdy klawisz zacinaloby pole, wiec podglad liczy sie na
  // wartosci odroczonej: pole odpowiada od razu, podglad dogania.
  const tekstDoPodgladu = tryb === "plik" ? (plikTekstu?.tekst ?? "") : tryb === "tekst" ? tekst : "";
  const odroczony = useDeferredValue(tekstDoPodgladu);
  const podglad = useMemo(
    () => (tryb === "nagranie" ? ({ rodzaj: "PUSTY" } as const) : podgladTekstu(odroczony)),
    [tryb, odroczony],
  );
  const opisWymagany = podglad.rodzaj === "BEZ_PODPISOW";
  /**
   * "Kim jestes": wybrany mowca, ale tylko wtedy, gdy jest nadal w tekscie (po poprawce tekstu jego podpis moze
   * zniknac) i tekst ma podpisy. Przy nagraniu i tekscie bez podpisow nikt nie jest wybrany: pytamy na ekranie wyniku.
   */
  const jaWybrany =
    uzytkownik && tryb !== "nagranie" && podglad.rodzaj === "PODPISANY" && ja !== null && podglad.mowcy.includes(ja)
      ? ja
      : null;

  function wyczyscBlad(pole: PoleFormularza): void {
    setBledy((b) => {
      if (b[pole] === undefined) return b;
      const { [pole]: _usuniete, ...reszta } = b;
      return reszta;
    });
    setBladOgolny(null);
  }

  function zmienTryb(nowy: TrybWejscia): void {
    if (busy || !czyTrybWidoku(nowy)) return;
    setTryb(nowy);
    setBledy({});
    setBladOgolny(null);
  }

  /**
   * Usuniety plik zabiera ze soba przycisk "Usun plik", ktory mial fokus - bez tego fokus
   * klawiatury spada na <body> i uzytkownik zaczyna Tab od poczatku strony (A-4). Strefa wyboru
   * pliku jest naturalnym nastepnym krokiem ("wybierz inny"), a sama nie znika razem z wierszem.
   */
  function fokusNaStrefePliku(): void {
    formularzRef.current?.querySelector<HTMLElement>('[data-pole="plik"] .upload-zone')?.focus();
  }

  function wybierzNagranie(wybrany: File): void {
    // Limity jeszcze nie przyszly (np. pierwsza proba sie nie udala): druga okazja, zanim pojedzie wielki plik.
    if (limity === null) wczytajLimity();
    // Wybor innego pliku zamyka sprawe poprzedniego: jego niedokonczone wgrywanie (po bledzie polaczenia) nikt juz nie wznowi.
    if (plikAudio && plikAudio !== wybrany) porzucZapamietaneWgrywanie(plikAudio);
    const blad = walidujNagranie(wybrany, limity?.limity ?? null);
    if (blad) {
      setPlikAudio(null);
      setBledy((b) => ({ ...b, plik: blad }));
      return;
    }
    setPlikAudio(wybrany);
    wyczyscBlad("plik");
  }

  async function wybierzPlikTekstu(wybrany: File): Promise<void> {
    const blad = walidujPlikTekstowy(wybrany);
    if (blad) {
      setPlikTekstu(null);
      setBledy((b) => ({ ...b, plik: blad }));
      return;
    }
    const odczyt = await czytajPlikTekstowy(wybrany);
    if ("blad" in odczyt) {
      setPlikTekstu(null);
      setBledy((b) => ({ ...b, plik: odczyt.blad }));
      return;
    }
    setPlikTekstu({ plik: wybrany, tekst: odczyt.tekst });
    wyczyscBlad("plik");
  }

  // Ocena brzmienia dla TEGO nagrania: dla dlugiego, ruszonego przez nikogo wyboru - "bez oceny". Wyliczane, nie
  // zapisywane: po usunieciu pliku formularz sam wraca do ustawien zapisanych.
  const czasSek = tryb === "nagranie" && plikAudio !== null && czasPliku?.plik === plikAudio ? czasPliku.sek : null;
  const wyborEkspresji = wyborEkspresjiDlaNagrania({
    czasSek,
    recznie: ekspresjaRecznie,
    aktualny: { dostawca: expression, model: expressionModel },
  });
  // Koszt grozi tylko, gdy ocena brzmienia w ogole jest wybrana (z ustawien albo recznie). Przy "brak" nie ma o czym ostrzegac.
  const ostrzezenieKosztu =
    czyAdmin && tryb === "nagranie" && czyDlugieNagranie(czasSek) && expression !== "brak"
      ? tekstOstrzezeniaKosztu(czasSek, wyborEkspresji.dostawca !== "brak")
      : null;

  const potwierdzenieTekst = uzytkownik ? potwierdzenieWgraniaUzytkownika : potwierdzenieWgrania;

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (busy) return;
    // Tryb spoza listy widoku nie jedzie na serwer, nawet gdyby stan go mial. Jedyny taki tryb to dzis nagranie w widoku
    // uzytkownika, stad zdanie o nagraniach (to samo co przy odmowie 403 z serwera).
    if (!czyTrybWidoku(tryb)) {
      setBladOgolny(KOMUNIKAT_NAGRANIA_WYLACZONE);
      return;
    }

    // Blad pliku wykryty przy wyborze (za duzy, nie ten typ, zle kodowanie) zostaje, dopoki
    // uzytkownik nie wybierze nowego: bez pliku sama walidacja powiedzialaby tylko "wybierz plik".
    const brakPliku = (tryb === "nagranie" && plikAudio === null) || (tryb === "plik" && plikTekstu === null);

    const znalezione = walidujFormularz({
      tryb,
      maPlikAudio: plikAudio !== null,
      maPlikTekstu: plikTekstu !== null,
      tekst: tryb === "plik" ? (plikTekstu?.tekst ?? "") : tekst,
      bladPliku: brakPliku ? (bledy.plik ?? null) : null,
      opis,
      zgoda,
      wybraneParametry: wybrane,
    });
    // Bez wczytanej listy "nic nie zaznaczono" nie jest bledem uzytkownika: stan listy stoi
    // w jej karcie z przyciskiem ponowienia, a tu zostaje jedno zdanie przy wysylce.
    const listaGotowa = katalog.stan === "gotowy";
    if (!listaGotowa) delete znalezione.soczewki;
    if (uzytkownik) {
      // Uzytkownik nie wybiera parametrow, wiec blad "zaznacz parametr" nie ma przy czym stanac. Pusty katalog
      // zalatwia zdanie przy wysylce nizej. Zdanie o braku opisu mowi jego jezykiem (bez slowa "model").
      delete znalezione.soczewki;
      if (znalezione.opis === KOMUNIKAT_BRAKU_OPISU) znalezione.opis = KOMUNIKAT_BRAKU_OPISU_UZYTKOWNIKA;
    }

    const wszystkie = znalezione;

    setBledy(wszystkie);
    const pierwsze = pierwszeZBledem(wszystkie);
    if (pierwsze) {
      setBladOgolny(null);
      setFokus((f) => ({ pole: pierwsze, numer: (f?.numer ?? 0) + 1 }));
      return;
    }
    if (!listaGotowa) {
      setBladOgolny(
        uzytkownik
          ? "Jeszcze przygotowujemy ocenę. Spróbuj ponownie za chwilę."
          : "Najpierw wczytaj listę parametrów oceny: bez niej nie wiemy, co ocenić.",
      );
      return;
    }
    if (uzytkownik && wybrane.length === 0) {
      setBladOgolny("Ocena jest teraz niedostępna. Spróbuj ponownie później albo daj nam znać.");
      return;
    }

    setBusy(true);
    setBladOgolny(null);
    try {
      const wspolne = { title: "", opis, zgoda, soczewki: wybrane };
      // Wybor "Ty" jedzie tylko z tekstem z podpisami: przy nagraniu mowcow jeszcze nie ma.
      const mowcaUzytkownika = jaWybrany ? { mowcaUzytkownika: jaWybrany } : {};
      let odpowiedz;
      if (tryb === "nagranie") {
        // Nagranie idzie przez lib/wgrywanie: male jednym zadaniem, duze porcjami z wznowieniem i postepem.
        const plik = plikAudio as File;
        const kontroler = new AbortController();
        kontrolerRef.current = kontroler;
        fazaRef.current = POSTEP_PUSTY.faza;
        setPostep({ ...POSTEP_PUSTY, razemBajtow: plik.size });
        odpowiedz = await wyslijNagranie(
          plik,
          {
            ...wspolne,
            audioRetention: retention,
            sttProvider: stt,
            expressionProvider: wyborEkspresji.dostawca,
            sttModel,
            expressionModel: wyborEkspresji.model,
          },
          {
            signal: kontroler.signal,
            onPostep: (p) => {
              fazaRef.current = p.faza;
              setPostep(p);
            },
          },
        );
      } else if (tryb === "plik") {
        odpowiedz = await api.upload({
          ...wspolne,
          ...mowcaUzytkownika,
          rodzaj: "TEXT",
          plikTekstu: (plikTekstu as { plik: File }).plik,
        });
      } else {
        odpowiedz = await api.upload({ ...wspolne, ...mowcaUzytkownika, title: tytul, rodzaj: "TEXT", tekst });
      }

      // Strona zniknela w fazie skladania (K-1): analiza powstala, a o nowej strony nie ma czego odswiezac ani czyscic.
      // Potwierdzenie idzie toastem, bo ten zyje ponad trasami.
      if (!zamontowanyRef.current) {
        toastRef.current.sukces(potwierdzenieTekst(tryb, odpowiedz.podpisany));
        return;
      }
      setPlikAudio(null);
      setPlikTekstu(null);
      setTekst("");
      setTytul("");
      setOpis("");
      // Zgoda dotyczy konkretnej rozmowy, wiec kolejna wymaga nowego zaznaczenia.
      setZgoda(false);
      setJa(null);
      setBledy({});
      // TO-1: krotkie potwierdzenie ZDARZENIA. Stan analizy niesie juz wiersz listy.
      toast.sukces(potwierdzenieTekst(tryb, odpowiedz.podpisany));
      await onWgrano(odpowiedz);
    } catch (err) {
      if (!zamontowanyRef.current) {
        // Wyjscie ze strony w trakcie porcji: komponent juz nie istnieje, a toast o przerwaniu poszedl z jego sprzatania.
        // Wyjscie w fazie skladania: toast mowil, ze analiza powstaje, wiec jesli serwer odrzucil plik, trzeba to powiedziec.
        if (!(err instanceof AnulowanoWgrywanie)) {
          toastRef.current.ostrzezenie(bladSerweraNaPole(err, tryb, undefined, czyAdmin, wariant).tekst);
        }
        return;
      }
      if (err instanceof AnulowanoWgrywanie) {
        // Anulowanie to decyzja uzytkownika, nie blad: bez czerwonego komunikatu. Plik i pola zostaja do ponownej proby,
        // a fokus wraca na przycisk wysylki, bo "Anuluj wysylanie" znika razem z paskiem (A-4).
        toast.informacja("Wysyłanie anulowane. Nagranie nie trafiło do analizy.");
        formularzRef.current?.querySelector<HTMLElement>(".btn-hero")?.focus();
        return;
      }
      const maxBajtow = limityRef.current?.maxBajtowNagrania;
      const blad = bladSerweraNaPole(
        err,
        tryb,
        maxBajtow === undefined ? undefined : Math.round(maxBajtow / 1024 / 1024),
        czyAdmin,
        wariant,
      );
      if (blad.odswiezParametry) {
        setRecznyWybor(null);
        ponowKatalog();
      }
      if (blad.odswiezListeAnaliz) void Promise.resolve(onWgrano()).catch(() => undefined);
      if (uzytkownik && blad.pole === "soczewki") {
        // Uzytkownik nie ma pola z parametrami, wiec komunikat o nich nie mialby przy czym stanac (ani by cos mowil).
        setBladOgolny("Nie udało się przygotować oceny. Odświeżyliśmy ją - spróbuj jeszcze raz.");
      } else if (blad.pole === "ogolny") {
        setBladOgolny(blad.tekst);
      } else {
        const pole = blad.pole;
        setBledy({ [pole]: blad.tekst });
        setFokus((f) => ({ pole, numer: (f?.numer ?? 0) + 1 }));
      }
    } finally {
      kontrolerRef.current = null;
      fazaRef.current = null;
      setPostep(null);
      setBusy(false);
    }
  };

  const etykietaDostawcy = (lista: ProviderLists["stt"], id: string): string => {
    if (id === "brak") return "bez oceny";
    return lista.find((p) => p.id === id)?.label ?? "nie wybrano";
  };

  // Konto `user` nie dostaje listy dostawcow (403), wiec pusta lista nie znaczy dla niego "brak modelu transkrypcji".
  const brakModeli = czyAdmin && providers !== null && providers.stt.length === 0;
  /** Stan listy modeli blokuje TYLKO tryb nagrania; teksty nie potrzebuja transkrypcji. */
  const nagranieNiegotowe = tryb === "nagranie" && (bladDostawcow !== null || providers === null || brakModeli);

  const dlugoscOpisu = opis.trim().length;
  const podpowiedzOpisu = uzytkownik
    ? opisWymagany
      ? "Wymagany: w tym tekście nie ma podpisów mówców, więc potrzebujemy opisu, żeby przypisać wypowiedzi do rozmówców. Napisz też, kto z kim już się zna albo rozmawiał."
      : tryb === "nagranie"
        ? "Opcjonalny. Z opisem lepiej rozpoznamy, kto mówi, a ocena będzie wiedziała, kto z kim rozmawia i kto z kim już się zna."
        : "Opcjonalny. Pomaga zrozumieć, kto rozmawia, o czym i kto z kim już się zna."
    : opisWymagany
      ? "Wymagany: w tym tekście nie ma podpisów mówców, więc model przypisze je na podstawie tego opisu. Napisz też, kto z kim już się zna albo rozmawiał."
      : tryb === "nagranie"
        ? "Opcjonalny. Z opisem model zaproponuje nazwy mówców, a ocena będzie wiedziała, kto z kim rozmawia i kto z kim już się zna."
        : "Opcjonalny. Pomaga modelowi zrozumieć, kto rozmawia, o czym i kto z kim już się zna.";

  // --- Kawalki widoku wspolne dla obu wariantow. Administrator sklada je w jedna karte, uzytkownik w trzy. ---

  const przelacznikTrybu = (
    <div className="wejscie-tryb">
      <SegmentedControl
        etykieta="Rodzaj materiału"
        opcje={trybyWidoku.map((t) => ({
          wartosc: t.wartosc,
          etykieta: t.etykieta,
        }))}
        wartosc={tryb}
        onZmiana={zmienTryb}
      />
    </div>
  );

  const powodBlokadyStrefy = uzytkownik ? "Trwa wysyłanie poprzedniej rozmowy." : "Trwa wysyłanie poprzedniej analizy.";

  const polaWejscia = (
    <>
      {tryb === "nagranie" && providers && (
        <div data-pole="plik">
          <UploadZone
            wymagania={tekstWymaganNagrania(limity)}
            accept="audio/*,video/*"
            onPlik={wybierzNagranie}
            wylaczona={busy}
            powodWylaczenia={powodBlokadyStrefy}
            blad={bledy.plik}
          />

          {/* UP-3 po korekcie T-5: wybrany plik to wiersz w polaczonej ramce.
              Usuniecie NIE idzie przez D-4, bo nic jeszcze nie zostalo wyslane -
              DG-4 wprost zwalnia z potwierdzenia to, co jest odwracalne. */}
          {plikAudio && (
            <div className="wybrany-plik">
              <ListaPlikow
                pliki={[{ id: "wybrany", nazwa: plikAudio.name, meta: formatujRozmiar(plikAudio.size) }]}
                onUsun={() => {
                  // Usuniecie pliku po bledzie polaczenia porzuca tez niedokonczone wgrywanie na serwerze.
                  porzucZapamietaneWgrywanie(plikAudio);
                  setPlikAudio(null);
                  wyczyscBlad("plik");
                  fokusNaStrefePliku();
                }}
              />
            </div>
          )}

          {/* Dwa wybory modeli schodza do dialogu D-1 za jedna linie podsumowania:
              inaczej formularz spycha liste analiz pod fold. F-2 wprost wylacza
              z wymogu symetrii paski "tresc + akcja". */}
          {czyAdmin && (
            <div className="modele-wybor">
              <p className="modele-wybor__tekst">
                Transkrypcja: {etykietaDostawcy(providers.stt, stt)} - Brzmienie:{" "}
                {etykietaDostawcy(providers.expression, wyborEkspresji.dostawca)}
              </p>
              <Button wariant="secondary" onClick={() => setDialogModeli(true)} wylaczony={busy}>
                Zmień modele
              </Button>
            </div>
          )}

          <Field
            etykieta="Co zrobić z nagraniem po analizie"
            id="retencja"
            hint="Transkrypcja i pomiary zostają niezależnie od tego wyboru."
            waskie
          >
            {(atrybuty) => <Select {...atrybuty} wartosc={retention} opcje={RETENCJE} onZmiana={setRetention} />}
          </Field>
        </div>
      )}

      {tryb === "plik" && (
        <div data-pole="plik">
          <UploadZone
            wymagania={POMOC_PLIKU_TEKSTOWEGO}
            accept=".txt,text/plain"
            onPlik={(p) => void wybierzPlikTekstu(p)}
            wylaczona={busy}
            powodWylaczenia={powodBlokadyStrefy}
            tytul="Przeciągnij plik z rozmową tutaj lub kliknij, aby wybrać"
            blad={bledy.plik}
          />
          {plikTekstu && (
            <div className="wybrany-plik">
              <ListaPlikow
                pliki={[{ id: "wybrany", nazwa: plikTekstu.plik.name, meta: formatujRozmiar(plikTekstu.plik.size) }]}
                onUsun={() => {
                  setPlikTekstu(null);
                  wyczyscBlad("plik");
                  fokusNaStrefePliku();
                }}
              />
            </div>
          )}
        </div>
      )}

      {tryb === "tekst" && (
        <div data-pole="tekst">
          <Input
            etykieta={uzytkownik ? "Nazwa rozmowy" : "Nazwa analizy"}
            id="wejscie-tytul"
            // Uzytkownik: bez nazwy serwer daje "Rozmowa z <dzien>", nigdy nazwe pliku (docs/konta.md). Nazwa trafia na
            // liste i do raportu, wiec prosimy, zeby nie niosla nazwisk.
            hint={
              uzytkownik
                ? "Opcjonalna, bez nazwisk. Bez niej nazwiemy ją Rozmowa z dzisiejszą datą."
                : "Opcjonalna. Bez niej analiza dostanie nazwę Rozmowa (tekst)."
            }
            maxLength={200}
            waskie
            value={tytul}
            onChange={(e) => setTytul(e.target.value)}
          />
          <Textarea
            etykieta="Tekst rozmowy"
            id="wejscie-tekst"
            hint={
              uzytkownik
                ? "Każda wypowiedź w nowej linii w postaci Rola: tekst. Zamiast imion wpisz role albo litery, np. Klient, A. Czas na początku linii (np. 05:12) jest opcjonalny. Tekst bez podpisów też przyjmiemy."
                : "Każda wypowiedź w osobnej linii w postaci Mówca: tekst. Czas na początku linii (np. 05:12) jest opcjonalny. Tekst bez podpisów też przyjmiemy."
            }
            blad={bledy.tekst}
            placeholder={TEKST_PLACEHOLDER}
            value={tekst}
            onChange={(e) => {
              setTekst(e.target.value);
              wyczyscBlad("tekst");
            }}
            rows={uzytkownik ? 6 : 12}
            disabled={busy}
          />
        </div>
      )}
    </>
  );

  const poleOpisu = (
    <div data-pole="opis">
      <Textarea
        etykieta="Opis rozmowy"
        id="wejscie-opis"
        hint={`${podpowiedzOpisu} (${dlugoscOpisu} z ${MAX_ZNAKOW_OPISU} znaków)`}
        blad={bledy.opis}
        placeholder={OPIS_PLACEHOLDER}
        value={opis}
        onChange={(e) => {
          setOpis(e.target.value);
          wyczyscBlad("opis");
        }}
        rows={4}
        disabled={busy}
      />
    </div>
  );

  // Zgoda to komunikat przy polu (F-12), nie wygaszony przycisk: uzytkownik ma
  // zobaczyc, czego brakuje, a nie zgadywac, czemu przycisk nie dziala.
  const poleZgody = (
    <div className="field" data-pole="zgoda">
      <Checkbox
        nazwa="consent"
        etykieta="Rozmówcy wiedzą o analizie i wyrazili na nią zgodę"
        opis="Wymagane przy każdej analizie. Treść rozmowy jest przetwarzana przez zewnętrznych dostawców modeli."
        zaznaczony={zgoda}
        wylaczony={busy}
        onZmiana={(z) => {
          setZgoda(z);
          wyczyscBlad("zgoda");
        }}
      />
      {bledy.zgoda && (
        <span className="field-error" role="alert">
          <IconOstrzezenie size={14} />
          {bledy.zgoda}
        </span>
      )}
    </div>
  );

  const bladOgolnyEl = bladOgolny && (
    <p className="field-error wysylka-blad" role="alert">
      <IconOstrzezenie size={14} />
      {bladOgolny}
    </p>
  );

  // PR-3: postep w tej samej karcie, nie nakladka; "Anuluj wysylanie" jest jego czescia.
  const postepEl = busy && tryb === "nagranie" && postep && (
    <PostepWysylania postep={postep} onAnuluj={() => kontrolerRef.current?.abort()} />
  );

  // Nazwiska i nazwy firm w materiale to dane osob trzecich: prosimy o ich zamiane, zanim material wyjedzie. Zdanie
  // zamiast linku do poradnika, ktorego nie ma (martwy link wygladalby na usterke). Stoi w obu widokach i przy kazdym
  // rodzaju materialu, takze przy nagraniu, ktore dzis wgrywa tylko administrator. BN-3: to informacja, wiec nigdy nie
  // staje jako drugi baner: ustepuje ostrzezeniu o koszcie (tylko u administratora) i banerowi strony (`stronaMaBaner`).
  const banerNazwisk = !ostrzezenieKosztu && !stronaMaBaner && (
    <div className="baner-nazwisk">
      <Banner wariant="info" tekst={tekstBaneruNazwisk(tryb)} />
    </div>
  );

  // --- Widok uzytkownika: trzy karty-siostry w jednym formularzu (makieta "Nowa rozmowa"). ---
  if (uzytkownik) {
    const mowcyZTekstu = tryb !== "nagranie" && podglad.rodzaj === "PODPISANY" ? podglad.mowcy : [];
    const idNiktZNich = "zaden";
    const opcjeJa = [
      ...mowcyZTekstu.map((m, i) => ({ wartosc: `m${i}`, etykieta: m })),
      { wartosc: idNiktZNich, etykieta: "Żaden z nich" },
    ];
    const indeksJa = jaWybrany === null ? -1 : mowcyZTekstu.indexOf(jaWybrany);
    const wartoscJa = indeksJa >= 0 ? `m${indeksJa}` : idNiktZNich;

    return (
      <form ref={formularzRef} onSubmit={(e) => void submit(e)} noValidate>
        <Card tytul="1. Skąd jest rozmowa?">
          {przelacznikTrybu}

          {nagranieNiegotowe ? (
            <StanLadowania tekst="Przygotowuję formularz..." />
          ) : (
            <>
              {polaWejscia}
              {tryb !== "nagranie" && <PodgladRozpoznania podglad={podglad} wariant="uzytkownik" />}
              {banerNazwisk}
              {poleOpisu}
            </>
          )}
        </Card>

        <Card tytul="2. Kim jesteś w tej rozmowie?">
          {mowcyZTekstu.length > 0 ? (
            <>
              <WyborMowcy
                etykieta="Kim jesteś w tej rozmowie"
                opcje={opcjeJa}
                wartosc={wartoscJa}
                widocznaEtykieta={false}
                onZmiana={(w) => {
                  const i = w === idNiktZNich ? -1 : Number(w.slice(1));
                  setJa(i >= 0 ? (mowcyZTekstu[i] ?? null) : null);
                }}
              />
              <span className="field-hint nowa-ja-hint">
                Pokażemy ocenę przede wszystkim Twojej części rozmowy. Jeśli nie wskażesz nikogo, zapytamy o to przy wyniku.
              </span>
            </>
          ) : (
            <p className="nowa-ja-zdanie">Po przetworzeniu zapytamy, który mówca to Ty.</p>
          )}
        </Card>

        <Card>
          {poleZgody}
          {bladOgolnyEl}
          {postepEl}

          {/* Parametry oceny ida same (wszystkie wlaczone z katalogu): uzytkownik nic nie wybiera. Gdy katalog sie nie
              wczytal, wysylka i tak by sie nie udala (serwer odmawia bez slugow), wiec stan bledu stoi tu, z ponowieniem. */}
          {katalog.stan === "blad" && (
            <StanBledu
              tytul="Nie udało się przygotować oceny"
              opis="Bez tego nie możemy ocenić rozmowy. Spróbuj ponownie za chwilę."
              akcja={
                <Button wariant="secondary" onClick={ponowKatalog}>
                  Spróbuj ponownie
                </Button>
              }
            />
          )}

          <div className="nowa-start">
            <button
              type="submit"
              className="btn-hero"
              // B-5: blokada drugiego klikniecia bez natywnego `disabled`, ktory
              // zabralby fokus z przycisku i wyrzucil klawiature na poczatek strony.
              aria-disabled={busy || undefined}
              aria-busy={busy || undefined}
            >
              {busy && <Spinner rozmiar="s" />}
              {etykietaPrzyciskuOceny(busy)}
            </button>
          </div>
        </Card>
      </form>
    );
  }

  // --- Widok administratora: jedna karta "Nowa analiza". ---
  return (
    <Card tytul="Nowa analiza">
      {przelacznikTrybu}

      {/* S-5: stany listy modeli renderuja sie W TEJ KARCIE. Dotycza wylacznie nagrania. */}
      {nagranieNiegotowe ? (
        bladDostawcow !== null ? (
          <StanBledu
            tytul="Nie udało się wczytać listy modeli"
            opis="Bez niej nie wiemy, czym policzyć analizę nagrania. Spróbuj ponownie za chwilę albo wgraj tekst."
            szczegol={bladDostawcow}
            akcja={
              <Button wariant="secondary" onClick={() => void pobierzDostawcow()}>
                Spróbuj ponownie
              </Button>
            }
          />
        ) : providers === null ? (
          <StanLadowania tekst="Wczytuję listę modeli..." />
        ) : (
          <StanPusty
            tytul="Nie ma skonfigurowanego modelu transkrypcji"
            opis="Dodaj klucz dostawcy w ustawieniach serwera, a wgrywanie nagrań włączy się samo. Tekst możesz wgrać już teraz."
          />
        )
      ) : (
        <form ref={formularzRef} onSubmit={(e) => void submit(e)} noValidate>
          {polaWejscia}

          {tryb !== "nagranie" && <PodgladRozpoznania podglad={podglad} />}

          {banerNazwisk}

          {poleOpisu}

          <div data-pole="soczewki">
            <WyborParametrow
              katalog={katalog}
              wybrane={wybrane}
              onZmiana={(lista) => {
                setRecznyWybor(lista);
                wyczyscBlad("soczewki");
              }}
              onPonow={ponowKatalog}
              blad={bledy.soczewki}
              wylaczony={busy}
            />
          </div>

          {poleZgody}

          {bladOgolnyEl}

          {/* BN-1: ostrzezenie o koszcie to trwajacy STAN wybranego pliku, wiec baner (nie toast), tuz nad akcja, ktora
              ten koszt uruchamia. Znika sam, gdy plik jest krotszy, usuniety albo ocena brzmienia wylaczona. */}
          {ostrzezenieKosztu && <Banner wariant="warning" tekst={ostrzezenieKosztu} />}

          {postepEl}

          {/* B-6: jedyne miejsce w aplikacji z wypelnieniem marki - akcja, ktora
              uruchamia automatyzacje. B-2: obok niej nie stoi zaden drugi primary. */}
          <div className="hero-slot">
            <button
              type="submit"
              className="btn-hero"
              // B-5: blokada drugiego klikniecia bez natywnego `disabled`, ktory
              // zabralby fokus z przycisku i wyrzucil klawiature na poczatek strony.
              aria-disabled={busy || undefined}
              aria-busy={busy || undefined}
            >
              {busy && <Spinner rozmiar="s" />}
              {etykietaPrzyciskuWysylki(tryb, busy)}
            </button>
          </div>
        </form>
      )}

      {czyAdmin && providers && (
        <Dialog
          otwarty={dialogModeli}
          onZamknij={() => setDialogModeli(false)}
          tytul="Modele do tej analizy"
          akcje={
            <Button wariant="primary" onClick={() => setDialogModeli(false)}>
              Gotowe
            </Button>
          }
        >
          <p className="field-hint">
            Wybór dotyczy tylko tej jednej analizy. Ustawienia domyślne zostają bez zmian.
          </p>
          <ProviderPicker
            legend="Model transkrypcji"
            name="stt"
            providers={providers.stt}
            value={stt}
            onChange={(id) => {
              setStt(id);
              // Wersja nalezy do dostawcy - zerujemy ja w tym samym kroku.
              setSttModel("");
            }}
            model={sttModel}
            onModelChange={setSttModel}
          />
          <ProviderPicker
            legend="Model oceny brzmienia"
            name="expression"
            providers={providers.expression}
            value={wyborEkspresji.dostawca}
            onChange={(id) => {
              setExpression(id);
              setExpressionModel("");
              setEkspresjaRecznie(true);
            }}
            model={wyborEkspresji.model}
            onModelChange={(m) => {
              setExpressionModel(m);
              setEkspresjaRecznie(true);
            }}
            allowNone
          />
        </Dialog>
      )}
    </Card>
  );
}

/**
 * Wynik rozpoznania tekstu pod polem (P-4: nieoczywiste wyjasnione na miejscu). Blad parsera stoi
 * przy polu (F-12), wiec tu go nie powtarzamy; pusty tekst nie pokazuje nic.
 */
function PodgladRozpoznania({
  podglad,
  wariant = "administrator",
}: {
  podglad: ReturnType<typeof podgladTekstu>;
  /** "uzytkownik": zdania jego jezykiem ("Rozpoznaliśmy ..."), bez plakietek mowcow (stoja w karcie "Kim jestes"). */
  wariant?: "administrator" | "uzytkownik";
}): React.JSX.Element | null {
  if (podglad.rodzaj === "PUSTY" || podglad.rodzaj === "BLAD") return null;
  const uzytkownik = wariant === "uzytkownik";

  if (podglad.rodzaj === "BEZ_PODPISOW") {
    return (
      <div className="podglad-tekstu" data-variant="info" role="status">
        <span className="podglad-tekstu__ikona" aria-hidden="true">
          <IconInformacja size={16} />
        </span>
        <p className="podglad-tekstu__tekst">
          {uzytkownik ? tekstRozpoznaniaDlaUzytkownika(podglad) : podglad.podsumowanie}
        </p>
      </div>
    );
  }

  return (
    <div className="podglad-tekstu" data-variant="success" role="status">
      <span className="podglad-tekstu__ikona" aria-hidden="true">
        <IconPtaszek size={16} />
      </span>
      <div className="podglad-tekstu__tekst">
        <p>
          {uzytkownik ? tekstRozpoznaniaDlaUzytkownika(podglad) : podglad.podsumowanie}
          {!uzytkownik && podglad.maCzasy ? " Wypowiedzi mają czasy." : ""}
        </p>
        {!uzytkownik && (
          <div className="podglad-tekstu__mowcy">
            {podglad.mowcy.slice(0, 8).map((m) => (
              <Badge key={m}>{m}</Badge>
            ))}
          </div>
        )}
        {podglad.uwagi.length > 0 && (
          <ul className="podglad-tekstu__uwagi">
            {podglad.uwagi.map((u) => (
              <li key={u}>{u}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
